import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, PageLayout } from '@sentinel/ui';
import { takeResumedResult } from '@sentinel/api-client';
import { useSession } from '@sentinel/auth-core';
import { NameSection } from '../features/profile/components/name-section';
import { EmailSection } from '../features/profile/components/email-section';
import { PasswordSection } from '../features/profile/components/password-section';
import { isResumedEmailChange } from '../features/profile/resumed-email-change';
import type { PendingEmailChange, SectionFeedback } from '../features/profile/types';

export function ProfilePage() {
  const { t } = useTranslation('profile');
  const { user, refresh } = useSession();
  const [topError, setTopError] = useState<string | null>(null);
  const [topSuccess, setTopSuccess] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingEmailChange | null>(null);

  const feedback: SectionFeedback = useMemo(
    () => ({
      clear: () => {
        setTopError(null);
        setTopSuccess(null);
      },
      success: (m) => {
        setTopError(null);
        setTopSuccess(m);
      },
      error: (m) => {
        setTopSuccess(null);
        setTopError(m);
      },
    }),
    [],
  );

  // An SSO user's email change finishes after a full-page redirect to the
  // identity provider, so the mutation never resolved and the pending state was
  // never set. Pick the outcome up from the resumed-request channel.
  useEffect(() => {
    const consume = () => {
      const result = takeResumedResult();
      if (!result) return;
      if (isResumedEmailChange(result)) setPending({ address: null });
    };
    consume();
    window.addEventListener('sentinel:step-up-resumed', consume);
    return () => window.removeEventListener('sentinel:step-up-resumed', consume);
  }, []);

  return (
    <PageLayout title={t('title')} subtitle={t('subtitle')}>
      {topError ? (
        <Alert type="error" role="alert">
          {topError}
        </Alert>
      ) : null}
      {topSuccess ? (
        <Alert type="info" role="status">
          {topSuccess}
        </Alert>
      ) : null}

      {/* The fallbacks below are real states, not just belt and braces:
          account.tsx renders its shell during a session re-validation and for
          the one tick before an unauthenticated visitor is redirected, so both
          fields can be absent while this page is mounted. */}
      <NameSection
        currentName={user?.name ?? ''}
        onSaved={() => {
          // refresh() re-reads /auth/userinfo so the account menu shows the
          // new name. This is the first caller of useSession().refresh() in
          // the repo — account.security.tsx uses window.location.reload()
          // for the same purpose, which is heavier than a name change
          // deserves.
          void refresh();
        }}
        feedback={feedback}
      />
      <EmailSection
        currentEmail={user?.email ?? ''}
        pending={pending}
        onPending={setPending}
        feedback={feedback}
      />
      <PasswordSection managedByIdp={Boolean(user?.localAuthManagedByIdp)} feedback={feedback} />
    </PageLayout>
  );
}

export const Route = createFileRoute('/account/profile')({ component: ProfilePage });
